import { motion } from "framer-motion"
import { AlertCircle, Lightbulb, Sparkles, HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import type { FeedbackCategory } from "@/types/index"

interface CategoryOption {
  id: FeedbackCategory
  icon: React.ReactNode
  label: string
  microcopy: string
  iconBg: string
  iconColor: string
}

const OPTIONS: CategoryOption[] = [
  {
    id: "bug",
    icon: <AlertCircle className="w-5 h-5" />,
    label: "Algo está com problema",
    microcopy: "Não está funcionando como deveria",
    iconBg: "bg-red-500/15",
    iconColor: "text-red-500",
  },
  {
    id: "idea",
    icon: <Lightbulb className="w-5 h-5" />,
    label: "Tenho uma sugestão",
    microcopy: "Uma melhoria que faria diferença",
    iconBg: "bg-amber-500/15",
    iconColor: "text-amber-500",
  },
  {
    id: "feature",
    icon: <Sparkles className="w-5 h-5" />,
    label: "Quero uma funcionalidade",
    microcopy: "Algo novo que ainda não existe",
    iconBg: "bg-purple-500/15",
    iconColor: "text-purple-500",
  },
  {
    id: "help",
    icon: <HelpCircle className="w-5 h-5" />,
    label: "Não sei usar",
    microcopy: "Preciso de uma mãozinha aqui",
    iconBg: "bg-blue-500/15",
    iconColor: "text-blue-500",
  },
]

interface CategoryGridProps {
  onSelect: (category: FeedbackCategory) => void
}

export function CategoryGrid({ onSelect }: CategoryGridProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-primary)]">
          Como podemos ajudar?
        </h3>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
          Sua mensagem vai direto pro time de desenvolvimento.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((opt, idx) => (
          <motion.button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            className={cn(
              "group relative flex flex-col items-start gap-2 p-3 rounded-xl text-left",
              "backdrop-blur-[20px] bg-glass-card border border-glass-border",
              "hover:bg-glass-hover transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)]",
            )}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04, duration: 0.2 }}
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div
              className={cn(
                "w-9 h-9 rounded-lg flex items-center justify-center",
                opt.iconBg,
                opt.iconColor,
              )}
            >
              {opt.icon}
            </div>
            <div className="min-w-0 w-full">
              <p className="text-sm font-medium text-[var(--text-primary)] leading-tight">
                {opt.label}
              </p>
              <p className="text-[11px] text-[var(--text-secondary)] mt-1 leading-tight">
                {opt.microcopy}
              </p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  )
}
